import { Provider } from 'jotai';
import React from 'react';

import { OpenModalButton } from '~src/commons/components/button/open-modal-button';
import { getDeliveryTimeText } from '~src/commons/utils/delivery-time';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState, useSessionState } from '~src/global-state';

import { CategoryOption, Choice, DeliveryTime } from '../../api/api';
import { CategoryExtensionProps } from '../category/category-manager';
import { useCategoryState } from '../category/category-state';
import { constructUserChoice } from '../category/user-choice';
import { UserChoiceProvider, UserChoiceProviderChildProps } from '../category/user-choice-provider';
import { FullscreenComponent, FullscreenMode } from '../fullscreen-internals/fullscreen-mode';
import { colors } from '../theme/colors';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { useListState } from '../ui/list-state';
import { InlineTimeSlotExtension } from './inline-time-slot-extension';
import { TimeSlotList } from './time-slot-list';
import { TimeSlotModalLayout } from './time-slot-modal-layout';

interface TimeSlotModalProps {
  selectedChoice: Choice;
  selectedOption: CategoryOption;
  onClose: () => void;
  onSubmit: (option: DeliveryTime) => void;
}

export const TimeSlotModal: FullscreenComponent<TimeSlotModalProps> = ({
  selectedChoice,
  selectedOption,
  onClose,
  onSubmit,
}) => {
  const listState = useListState({
    items: selectedOption.delivery_times,
    getKey: (deliveryTime: DeliveryTime) => deliveryTime.id,
    defaultSelectedKey: selectedChoice.time_slot_ref,
  });

  const { tICU } = useTranslations();

  return (
    <Dialog width={520} height={600} onClose={onClose}>
      <TimeSlotModalLayout
        title={tICU('widget.DELIVERY_TIMES_MODAL_HEADER')}
        buttonElement={
          <Button type="submit" onClick={() => onSubmit(listState.getSelectedItem()!)}>
            {tICU('widget.ADDRESS_MODAL.SAVE_BTN')}
          </Button>
        }
        listElement={<TimeSlotList listState={listState} />}
        onClose={onClose}
      />
    </Dialog>
  );
};
TimeSlotModal.fullscreenName = 'TimeSlotModal';

export const TimeSlotExtension: React.FC<CategoryExtensionProps> = ({
  selected,
  selectedChoice,
  selectedOption,
}) => {
  const { tICU, formatDate, locale } = useTranslations();
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const state = useGlobalState();
  const category = useCategoryState();
  const session = useSessionState();

  if (
    !selected ||
    category.delivery_type !== 'delivery' ||
    category.delivery_time_properties?.keep_delivery_time_after_address ||
    selectedOption.delivery_times.length < 2
  ) {
    return null;
  }

  const selectedDeliveryTime = selectedOption.delivery_times!.find(
    deliveryTime => deliveryTime.id === selectedChoice.time_slot_ref
  );
  const timeText = getDeliveryTimeText(
    selectedDeliveryTime!,
    category.delivery_type,
    tICU,
    formatDate,
    locale,
    {
      isTomorrowHoliday: !!state.additional?.is_tomorrow_holiday,
      purchaseCountry: state.purchase_country,
      showDeliveryTimeAsEstimate: session.flags.show_delivery_times_as_estimates,
      alterIcsDeliveryTimeMessage: session.flags.alter_ics_delivery_message,
    }
  );

  if (session.flags.enable_inline_category_extensions) {
    return (
      <Provider>
        <InlineTimeSlotExtension
          timeText={timeText}
          selectedChoice={selectedChoice}
          selectedOption={selectedOption}
        />
      </Provider>
    );
  }

  const renderModal = ({ onUpdate }: UserChoiceProviderChildProps) => {
    if (!modalOpen) {
      return null;
    }

    return (
      <FullscreenMode>
        <TimeSlotModal
          selectedChoice={selectedChoice}
          selectedOption={selectedOption}
          onClose={() => setModalOpen(false)}
          onSubmit={(newDeliveryTime: DeliveryTime) => {
            setModalOpen(false);
            const newChoice = constructUserChoice(category, selectedOption, newDeliveryTime.id);
            onUpdate(newChoice);
          }}
        />
      </FullscreenMode>
    );
  };

  return (
    <>
      <OpenModalButton
        onClick={() => setModalOpen(true)}
        title={{ text: timeText, color: colors.black, textStyle: 'normal' }}
        ariaLabel={tICU('widget.DELIVERY_TIMES_MODAL_HEADER')}
      />

      <UserChoiceProvider>{renderModal}</UserChoiceProvider>
    </>
  );
};
